/**
 * Consolidated status utilities for events and tickets
 * Replaces getEventStatus, getEventStatusColor, and getStatusColor functions
 */

/**
 * Event state mapping
 * @param {number} stateFlag - Event state flag (1-5)
 * @returns {string} Human-readable status
 */
export const getEventStatus = (stateFlag) => {
	switch (stateFlag) {
		case 1:
			return 'Scheduled'
		case 2:
			return 'In progress'
		case 3:
			return 'Completed'
		case 4:
			return 'Taking'
		case 5:
			return 'Cancelled'
		default:
			return 'Unknown'
	}
}

/**
 * Event status color mapping
 * @param {number} stateFlag - Event state flag (1-5)
 * @returns {string} Color key for theme
 */
export const getEventStatusColor = (stateFlag) => {
	switch (stateFlag) {
		case 1:
			return 'info' // Scheduled
		case 2:
			return 'warning' // In progress
		case 3:
			return 'success' // Completed
		case 4:
			return 'success' // Taking
		case 5:
			return 'error' // Cancelled
		default:
			return 'info'
	}
}

/**
 * Get color value from theme based on status color key
 * @param {string} statusColor - Color key (info, warning, success, error, etc.)
 * @param {object} theme - Theme object with color definitions
 * @returns {string} Actual color value
 */
export const getStatusColor = (statusColor, theme) => {
	const colors = {
		// Event status colors
		info: theme?.color?.info || '#3B82F6',
		warning: theme?.color?.warning || '#F59E0B',
		success: theme?.color?.success || '#10B981',
		error: theme?.color?.error || '#EF4444',

		// Additional status colors
		participating: theme?.color?.black || '#1F2024',
		inProgress: theme?.color?.major || '#ED3241',
		default: theme?.color?.grey3 || '#B2B2B2',
		white: theme?.color?.white || '#FFFFFF',
		upcoming: '#1F2024',
		end: '#8D8D8D',
		// Ticket status colors
	}

	return colors[statusColor] || colors.default
}

/**
 * Combined function that returns both event status and color
 * @param {number} stateFlag - Event state flag (1-5)
 * @param {object} theme - Theme object
 * @returns {object} { status: string, colorKey: string, colorValue: string }
 */
export const getEventStatusInfo = (stateFlag, theme) => {
	const status = getEventStatus(stateFlag)
	const colorKey = getEventStatusColor(stateFlag)
	const colorValue = getStatusColor(colorKey, theme)

	return {
		status,
		colorKey,
		colorValue,
	}
}

/**
 * Get ticket status based on usage
 * @param {boolean} isUsed - Whether ticket is used
 * @returns {object} { status: string, colorKey: string }
 */
export const getTicketStatusInfo = (isUsed) => {
	return {
		status: isUsed ? 'Used' : 'Not used',
		colorKey: isUsed ? 'error' : 'success',
	}
}
